import { useState } from 'react';
import Timer from 'tiny-timer';
import Logger from '../Logger/Logger';
import TimerEvent from '../Types/TimerEvent';
import {CONNECTION_STATUS_TIMINGS_MILLISECONDS} from "../../Config/Parameters";

const logger = Logger.Create('useRefreshConnectionStatus');

/**
 * Provides the types that should be returned by the hook.
 */
type useRefreshConnectionStatusType =
{
    refreshConnection: () => void;
    stopRefresh: () => void;
};

/**
 * Create an instance of the tiny timer.
 */
const timer = new Timer({
    interval: CONNECTION_STATUS_TIMINGS_MILLISECONDS.interval,
});

/**
 * Hook for refreshing the connection status.
 * @param callback Callback when the timer interval has elapsed.
 */
const useRefreshConnectionStatus = (callback?: () => void): useRefreshConnectionStatusType =>
{
    const [isRefreshing, setIsRefreshing] = useState(false);

    const onTimerEnded = (): void =>
    {
        if (callback !== undefined)
        {
            callback();
        }
    };

    /**
     * Refresh the connection to the server.
     */
    const refreshConnection = (): void =>
    {
        // Wait for the existing timer to
        // complete before triggering it again...
        if (isRefreshing)
        {
            return;
        }

        setIsRefreshing(true);

        // Stop the current timer if one was already running...
        stopRefresh();

        logger.info('Starting the timer...');
        timer.on(TimerEvent.Tick, onTimerEnded);
        timer.start(CONNECTION_STATUS_TIMINGS_MILLISECONDS.duration);
    };

    /**
     * Stop the timer.
     */
    const stopRefresh = (): void =>
    {
        setIsRefreshing(false);
        logger.trace('Disabling the timer...');
        timer.stop();
        timer.off(TimerEvent.Tick, onTimerEnded);
        logger.info('Successfully disabled the timer!');
    };

    return { refreshConnection, stopRefresh };
};

export default useRefreshConnectionStatus;